import angular from 'angular';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import * as actions from '../../../store/actions';

const mapStateToScope = (state) => ({
  activeOrganization: state.organization.activeOrganization,
});

angular
  .module('myConsultApp')
  .controller(
    'organizationCreateEditController',
    ($ngRedux, $scope, $rootScope, $timeout, $filter, $http, ENV) => {
      $scope.componentMode = 'creating';

      $scope.currentTab = 'organization';
      $scope.nextTab = '';
      $scope.tabs = ['organization', 'contact', 'location', 'subscription'];
      $scope.organizationTypes = [
        { name: 'Vet Practice', value: 1 },
        { name: 'University', value: 2 },
        { name: 'Sales Rep', value: 3 },
        { name: 'Government/Non-Profit', value: 4 },
        { name: 'Clinic', value: 5 },
      ];
      $scope.organizationStatus = [
        { name: 'Active', value: true },
        { name: 'Inactive', value: false },
      ];
      $scope.directoryTypes = [
        { name: 'Clinic', value: 'clinic' },
        { name: 'Shelter', value: 'shelter' },
      ];
      $scope.aahaAffiliationOptions = [
        { name: 'Member Accredited', value: 'AC' },
        { name: 'Member Non-Accredited', value: 'NA' },
        { name: 'Not Associated', value: 'NO' },
      ];
      $scope.appSubscriptions = [
        { name: 'Zoetis Petcare Rewards', slug: 'loyalty' },
        { name: 'Cat Pain IQ', slug: 'tigger' },
        { name: 'Experience Solensia', slug: 'solensia' },
        { name: 'Experience Librela', slug: 'librela-iep' },
      ];
      $scope.states = [];

      $scope.init = () => {
        $scope.unsubscribe = $ngRedux.connect(mapStateToScope, actions)($scope);
        $scope.loadStates();
      };

      $scope.showWarningModal = false;

      $scope.unsavedChangeWarningModalOptions = {
        heading: 'Warning!',
        message:
          'You have unsaved changes on this tab! Press OK to continue (undo changes), or Cancel to stay on the current tab and continue editing.',
        buttons: [
          {
            label: 'OK',
            href: '#',
            clickFn: () => {
              $scope.showWarningModal = false;
              $scope.changeTab();
            },
          },
          {
            label: 'Cancel',
            href: '#',
            clickFn: () => {
              $scope.showWarningModal = false;
              $scope.nextTab = '';
            },
          },
        ],
      };

      $scope.loadStates = () => {
        $scope.getOrganizationStates($http, ENV).then((states) => {
          $timeout(() => {
            $scope.states = states;
          });
        });
      };

      $scope.clearOrganizationCreate = () => {
        $scope.creatingOrganization = {
          name: '',
          is_active: true,
          type_id: '',
          mdm_id: '',
          phone: '',
          address_1: '',
          address_2: '',
          zipcode: '',
          city: '',
          state: '',
          latitude: '',
          longitude: '',
        };

        $scope.creatingSuccess = false;
        $scope.isCreating = false;

        if ($scope.createForm) {
          $scope.createForm.$setPristine();
          $scope.createForm.$setUntouched();
        }

        $scope.resetCreatingError();
      };

      $scope.resetCreatingError = () => {
        $scope.creatingError = {
          details: '',
          highlightedFields: {
            name: [],
            is_active: [],
            type_id: [],
            mdm_id: [],
            phone: [],
            address_1: [],
            address_2: [],
            zipcode: [],
          },
        };
      };

      $scope.submitOrganizationCreate = () => {
        $scope.isCreating = true;
        const data = {
          organization: $scope.sanitizeData({
            ...$scope.creatingOrganization,
            directory_type: ['clinic'],
            applications: [{ is_active: true, slug: 'loyalty' }],
          }),
        };
        if (!data.organization.latitude) {
          data.organization.latitude = null;
        }
        if (!data.organization.longitude) {
          data.organization.longitude = null;
        }
        if (!data.organization.phone) {
          delete data.organization.phone;
        }

        $scope.resetCreatingError();

        $scope
          .createOrganization($http, ENV, data)
          .then(() => {
            $scope.isCreating = false;
            $scope.getData();
            $scope.closeCreateEditOrganization();
          })
          .catch((error) => {
            $scope.isCreating = false;
            $scope.applyError(error, 'create');
          });
      };

      $scope.clearOrganizationEdit = () => {
        $scope.tabs.forEach((tab) => {
          $scope.resetOrganizationEditForm(tab);
          $scope.resetEditingError(tab);
        });
        $scope.currentTab = $scope.tabs[0];
        $scope.editingSuccess = false;
        $scope.isEditing = false;
      };

      $scope.applyError = (error, createOrEdit = 'create') => {
        $timeout(() => {
          const errorKey = createOrEdit === 'create' ? 'creatingError' : 'editingError';
          if (error.status === 500) {
            $scope[errorKey].details =
              'There was an error with your request. Please try again in a few minutes.';
          } else if (error.status === 400) {
            $scope[errorKey].details = 'Please fix the highlighted field(s).';
            $scope[errorKey].highlightedFields = error.data.organization;
            if (error.data.organization.non_field_errors) {
              $scope[errorKey].details = error.data.organization.non_field_errors[0];
            }
          } else {
            $scope[errorKey].details =
              get(error, 'data.organization.detail') || get(error, 'statusText');
          }
        });
      };

      $scope.getInitialFormData = (tabName) => {
        if (tabName === 'organization') {
          return {
            name: get($scope.activeOrganization, 'name', ''),
            type_id: get($scope.activeOrganization, 'type.id', ''),
            mdm_id: get($scope.activeOrganization, 'mdm_id', ''),
            aaha_affiliation: get($scope.activeOrganization, 'aaha_affiliation', ''),
            is_active: get($scope.activeOrganization, 'is_active', true),
            directory_type: $scope.activeOrganization
              ? $scope.activeOrganization.directory_type.map((directoryType) => directoryType.slug)
              : [],
            description: get($scope.activeOrganization, 'description', ''),
          };
        }

        if (tabName === 'contact') {
          return {
            email: get($scope.activeOrganization, 'email', ''),
            phone: get($scope.activeOrganization, 'phone', ''),
            practice_email: get($scope.activeOrganization, 'practice_email', ''),
            appointment_email: get($scope.activeOrganization, 'appointment_email', ''),
            url: get($scope.activeOrganization, 'url', ''),
            facebook: get($scope.activeOrganization, 'facebook', ''),
            twitter: get($scope.activeOrganization, 'twitter', ''),
            linkedin: get($scope.activeOrganization, 'linkedin', ''),
            appointment_link: get($scope.activeOrganization, 'appointment_link', ''),
            privacy_policy_link: get($scope.activeOrganization, 'privacy_policy_link', ''),
            general_opt_out_text: get($scope.activeOrganization, 'general_opt_out_text', ''),
            general_opt_out_link: get($scope.activeOrganization, 'general_opt_out_link', ''),
          };
        }

        if (tabName === 'location') {
          return {
            address_1: get($scope.activeOrganization, 'address_1', ''),
            address_2: get($scope.activeOrganization, 'address_2', ''),
            zipcode: get($scope.activeOrganization, 'zipcode', ''),
            latitude: get($scope.activeOrganization, 'latitude', ''),
            longitude: get($scope.activeOrganization, 'longitude', ''),
            city: get($scope.activeOrganization, 'city.name', ''),
            state: get($scope.activeOrganization, 'state.abbreviation', ''),
          };
        }

        return {
          applications: $scope.activeOrganization
            ? $scope.activeOrganization.applications.map((application) => application.slug)
            : [],
          tigger_notification_email: get(
            $scope.activeOrganization,
            'tigger_notification_email',
            '',
          ),
          antech_id: get($scope.activeOrganization, 'antech_id', ''),
          quest_id: get($scope.activeOrganization, 'quest_id', ''),
          vetdata_id: get($scope.activeOrganization, 'vetdata_id', ''),
          vetdata_code: get($scope.activeOrganization, 'vetdata_code', ''),
        };
      };

      $scope.resetOrganizationEditForm = (tabName) => {
        $scope.editingOrganization = $scope.getInitialFormData(tabName);

        $scope.editSuccess = false;

        $scope.nextTab = '';

        if ($scope.editForm) {
          $scope.editForm.$setPristine();
          $scope.editForm.$setUntouched();
        }

        $scope.resetEditingError(tabName);
      };

      $scope.resetEditingError = (tabName) => {
        if (tabName === 'organization') {
          $scope.editingError = {
            details: '',
            highlightedFields: {
              name: [],
              type_id: [],
              mdm_id: [],
              aaha_affiliation: [],
              is_active: [],
              directory_type: [],
              description: [],
            },
          };
        } else if (tabName === 'contact') {
          $scope.editingError = {
            details: '',
            highlightedFields: {
              email: [],
              phone: [],
              practice_email: [],
              appointment_email: [],
              url: [],
              facebook: [],
              twitter: [],
              linkedin: [],
              appointment_link: [],
              privacy_policy_link: [],
              general_opt_out_text: [],
              general_opt_out_link: [],
            },
          };
        } else if (tabName === 'location') {
          $scope.editingError = {
            details: '',
            highlightedFields: {
              address_1: [],
              address_2: [],
              zipcode: [],
              latitude: [],
              longitude: [],
              city: [],
              state: [],
            },
          };
        } else if (tabName === 'subscription') {
          $scope.editingError = {
            details: '',
            highlightedFields: {
              applications: [],
              service_types: [],
              tigger_notification_email: [],
              antech_id: [],
              quest_id: [],
              vetdata_id: [],
              vetdata_code: [],
            },
          };
        }
      };

      $scope.onTabChange = (nextTab) => {
        if ($scope.curretTab !== nextTab) {
          $scope.nextTab = nextTab;

          const isFormChanged = $scope.checkFormChanged();

          if (isFormChanged) {
            $scope.showWarningModal = true;
          } else {
            $scope.changeTab();
          }
        }
      };

      $scope.checkFormChanged = () => {
        const initialFormData = $scope.getInitialFormData($scope.currentTab);

        return !isEqual(initialFormData, $scope.editingOrganization);
      };

      $scope.changeTab = () => {
        $scope.currentTab = $scope.nextTab;
        $scope.resetOrganizationEditForm($scope.nextTab);
      };

      $scope.setOrganizationData = () => {
        if ($scope.activeOrganization) {
          $scope.componentMode = 'editing';
          $scope.resetOrganizationEditForm($scope.currentTab);
        }
      };

      $scope.submitOrganizationEdit = () => {
        const data = {
          organization: $scope.sanitizeData($scope.editingOrganization),
        };
        const currentRewardId = $scope.activeOrganization.id;

        if (data.organization.applications) {
          data.organization.applications = data.organization.applications.map((elem) => ({
            is_active: true,
            slug: elem,
          }));
        }

        if ($scope.currentTab === 'subscription') {
          const applicationSlugs = data.organization.applications.map(
            (application) => application.slug,
          );

          if (!applicationSlugs.includes('tigger')) {
            delete data.organization.tigger_notification_email;
          }
        }

        data.organization.tigger_notification_opt_in = !!data.organization
          .tigger_notification_email;

        $scope.resetEditingError();

        $scope.isEditing = true;

        $scope
          .updateOrganization($http, ENV, currentRewardId, data)
          .then(() => {
            $timeout(() => {
              $scope.editingSuccess = true;
              $('.create-edit-organization-container').animate({ scrollTop: 0 });
              setTimeout(() => {
                $scope.editingSuccess = false;
              }, 5000);
              $scope.getData(true);
            });
          })
          .catch((error) => {
            $scope.applyError(error, 'edit');
          })
          .finally(() => {
            $timeout(() => {
              $scope.isEditing = false;
            });
          });
      };

      $scope.searchGeoCode = (field) => {
        const organizationData =
          $scope.componentMode === 'creating'
            ? $scope.creatingOrganization
            : $scope.editingOrganization;

        if (!organizationData[field]) {
          return;
        }

        if (
          (field === 'zipcode' &&
            organizationData.zipcode.length > 3 &&
            $scope.componentMode === 'creating') ||
          (field === 'zipcode' &&
            organizationData.zipcode.length > 3 &&
            $scope.activeOrganization.zipcode !== organizationData.zipcode) ||
          (field === 'city' &&
            get($scope.activeOrganization, 'city.name', '') !== organizationData.city) ||
          (field === 'state' &&
            get($scope.activeOrganization, 'state.abbreviation', '') !== organizationData.state) ||
          (field === 'address' &&
            $scope.activeOrganization.address_1 !== organizationData.address_1)
        ) {
          $scope.isGeoCoding = true;
          const addresses = [];
          if (organizationData.address_1) addresses.push(organizationData.address_1);
          if (organizationData.address_2) addresses.push(organizationData.address_2);

          let address;

          if (field === 'zipcode') {
            address = {
              address: addresses.toString(),
              city: '',
              state: '',
              zipcode: organizationData.zipcode,
            };
          } else if (field === 'city') {
            address = {
              address: addresses.toString(),
              city: organizationData.city,
              state: '',
              zipcode: organizationData.zipcode,
            };
          } else {
            address = {
              address: addresses.toString(),
              city: organizationData.city,
              state: organizationData.state,
              zipcode: organizationData.zipcode,
            };
          }

          $scope.getGeoCode($http, ENV, address).then((geoCode) => {
            $timeout(() => {
              if (Object.keys(geoCode).length === 0) {
                $scope[`${$scope.componentMode}Error`].highlightedFields[field] = [
                  `Invalid ${field}`,
                ];
                $scope.isGeoCoding = false;
                return;
              }

              $scope[`${$scope.componentMode}Organization`].latitude = geoCode.latitude;
              $scope[`${$scope.componentMode}Organization`].longitude = geoCode.longitude;
              $scope[`${$scope.componentMode}Organization`].city = geoCode.city;
              $scope[`${$scope.componentMode}Organization`].state = geoCode.state;

              if (
                $scope.componentMode === 'edit' &&
                ((field === 'zipcode' &&
                  geoCode.zipcode !== $scope[`${$scope.componentMode}Organization`].zipcode) ||
                  (field === 'city' &&
                    geoCode.city !== $scope[`${$scope.componentMode}Organization`].city) ||
                  (field === 'state' &&
                    geoCode.state !== $scope[`${$scope.componentMode}Organization`].state))
              ) {
                $scope[`${$scope.componentMode}Organization`].latitude = '';
                $scope[`${$scope.componentMode}Organization`].longitude = '';
                $scope[`${$scope.componentMode}Organization`].city = '';
                $scope[`${$scope.componentMode}Organization`].state = '';
                $scope[`${$scope.componentMode}Error`].highlightedFields.zipcode = [
                  'The coordinates for this address could not be found. Please try changing the state or manually populate the latitude and longitude for this organization.',
                ];
              }
              $scope.isGeoCoding = false;
            });
          });
        }
      };

      $scope.sanitizeData = (data) => {
        return Object.keys(data).reduce((acc, key) => {
          acc[key] = data[key] === '' ? null : data[key];
          return acc;
        }, {});
      };

      $scope.clearErrorField = (valueKey) => {
        $scope[`${$scope.componentMode}Error`].highlightedFields[valueKey] = [];
        $scope[`${$scope.componentMode}Error`].details = '';
      };

      $scope.clearOrganizationCreateEdit = () => {
        $scope.clearOrganizationCreate();
        $scope.clearOrganizationEdit();
        $scope.componentMode = 'creating';
        $scope.isGeoCoding = false;
      };

      $scope.closeCreateEditOrganization = () => {
        $scope.clearOrganizationCreateEdit();
        $rootScope.closeComponent();
      };

      $scope.init();
      $scope.clearOrganizationCreateEdit();

      const activeOrganizationListener = $scope.$watch('activeOrganization', () => {
        if ($scope.activeOrganization) {
          $scope.setOrganizationData();
        } else {
          $scope.clearOrganizationEdit();
        }
      });

      const componentVisibleListener = $rootScope.$watch('componentOpened', () => {
        if (!$rootScope.componentOpened) {
          $scope.clearOrganizationCreateEdit();
          $scope.clearOrganization();
        }
      });

      $scope.$on('$destroy', () => {
        $scope.unsubscribe();
        $scope.clearOrganizationCreateEdit();
        componentVisibleListener();
        activeOrganizationListener();
      });
    },
  )
  .directive('organizationCreateEdit', () => {
    return {
      templateUrl: 'templates/v2/organization-create-edit.html',
    };
  });
